import { useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useVoiceVisualizer, VoiceVisualizer } from "react-voice-visualizer";
import { MIME_TYPE } from "../../services/voiceRecorderService";
import { WaveformSession } from "../../types/VoiceRecorder";

const MEDIA_RECORDER_OPTIONS: MediaRecorderOptions = {
  mimeType: MIME_TYPE,
  audioBitsPerSecond: 64_000,
};
const MEDIA_RECORDER_TIMESLICE_MS = 1000;

type VoiceRecorderWaveformProps = {
  session: WaveformSession;
};

// Private worker component of useVoiceRecorder — do not render it directly: it opens the
// microphone on mount and relies on the hook's session semantics (settled permission,
// key={session.id} remounts).
//
// react-voice-visualizer keeps recorder, stream and analyser state inside this hook, which
// re-renders at animation frame rate while recording. Hosting it in this leaf component keeps
// that render load away from the chat input; the parent hook talks to it via the session object.
export default function VoiceRecorderWaveform({ session }: VoiceRecorderWaveformProps) {
  const theme = useTheme();
  const controls = useVoiceVisualizer({
    // The library's own beforeunload handler would block every unload with a confirmation
    // prompt; page-exit cleanup is deliberately omitted (see useVoiceRecorder).
    shouldHandleBeforeUnload: false,
    mediaRecorderOptions: MEDIA_RECORDER_OPTIONS,
    timeslice: MEDIA_RECORDER_TIMESLICE_MS,
    onChunkAvailable: (chunk) => session.chunk(chunk),
  });
  const { error, isRecordingInProgress, startRecording } = controls;
  const startedRef = useRef(false);

  // Latest-value publish, idempotent and therefore safe during render: stopping the recorder
  // must always reach the controls instance of the current render, never a stale one.
  session.attachControls(controls);

  // Keep because of StrictMode - won't work on local development otherwise
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      startRecording();
    }
  }, []);

  // Bridges the library's committed state into the session. Observing state is deliberate:
  // the library's onStartRecording callback fires before its state commits — stopRecording()
  // would be a no-op there, so a session cancelled during the pending start could not be torn
  // down — and recording errors have no callback at all. Re-runs are harmless: the session's
  // phase guards ignore repeated started()/recorderError() calls.
  useEffect(() => {
    if (error) {
      session.recorderError();
      return;
    }
    if (isRecordingInProgress) {
      session.started();
    }
  }, [isRecordingInProgress, error]);

  return (
    <VoiceVisualizer
      controls={controls}
      height={32}
      mainBarColor={theme.palette.text.secondary}
      barWidth={3}
      gap={1}
      rounded={1}
      fullscreen
      onlyRecording
      isControlPanelShown={false}
      isDefaultUIShown={false}
    />
  );
}
